import { PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as analysisState from '@/components/Analysis/state';
import { useRouter } from '@/hooks';
import { abyssColors } from '@/theme/colors';
import { PointOfInterestChecked, PointOfInterestGroupBy } from '@/types';
import { Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { notEmpty } from '@/utils';
import { AllEquipmentBackButton } from '../../modules/pointOfInterest';
import { PointsOfInterestList } from './PointsOfInterestList';
import { PoiTypeAccordion } from './PoiTypeAccordion';
import { PoiTypeAccordionSkeleton } from './PoiTypeAccordion/PoiTypeAccordionSkeleton';
import { SearchBar } from './SearchBar';
import { Container, ScrollableContainer } from './styles';
import { AddToReportButton } from '../../modules/pointOfInterest/PointOfInterestTab/AddToReportButton';

export const PointsOfInterestByType = ({
  pois,
  changeTab,
  allPOILoading,
}: {
  pois: PointOfInterestChecked[];
  changeTab: (selectedTabIndex: number) => void;
  allPOILoading?: boolean;
}) => {
  const allTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);
  const selectedSpherical = useAtomValue(analysisState.selectedSpherical);
  const assemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const setPoiFilteredByCheckbox = useSetAtom(poiState.poiFilteredByCheckbox);
  const [checkedGroupedPois, setCheckedGroupedPois] = useAtom(analysisState.checkedGroupedPois);
  const arePoisVisible = useAtomValue(analysisState.arePoisVisible);
  const [searchTerm, setSearchTerm] = useState('');

  const router = useRouter();

  const isAtInsightsPage = router.pathname.includes('insights');

  const groupByArgumentsMemo: PointOfInterestGroupBy[] = useMemo(() => {
    return pois.map((poi) => {
      return {
        ...poi,
        checked: isAtInsightsPage || arePoisVisible,
      };
    });
  }, [pois, isAtInsightsPage, arePoisVisible]);

  const groupedPois = useMemo(
    () =>
      groupByArgumentsMemo.reduce<Record<string, PointOfInterestGroupBy[]>>((accumulator, poi) => {
        const templateId = String(poi.templateId);
        if (!accumulator[templateId]) {
          accumulator[templateId] = [];
        }
        accumulator[templateId].push(poi);
        return accumulator;
      }, {}),
    [groupByArgumentsMemo]
  );

  useEffect(() => {
    setCheckedGroupedPois(groupedPois);
  }, [groupedPois, arePoisVisible, assemblyId, setCheckedGroupedPois]);

  const handleSearchPoi = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    if (!searchTerm) {
      setCheckedGroupedPois(groupedPois);
      return;
    }

    const updatedGroupedPois = Object.keys(groupedPois).reduce((accumulator, templateId) => {
      const filteredPois = groupedPois[templateId].filter(
        (poi) =>
          (poi.poiId ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (poi.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (poi.assembly?.tagName ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      return { ...accumulator, [templateId]: filteredPois };
    }, {});
    setCheckedGroupedPois(updatedGroupedPois);
  };

  const filteredTemplates = allTemplates.filter(
    (template) => template.type !== PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
  );

  useEffect(() => {
    const arrayOfCheckedPoi = Object.values(checkedGroupedPois).map((templateArray) =>
      templateArray.filter((poi) => poi.checked)
    );
    setPoiFilteredByCheckbox(arrayOfCheckedPoi.filter(notEmpty).flat());
  }, [setPoiFilteredByCheckbox, checkedGroupedPois]);

  const isLoading = allPOILoading || allTemplates.length === 0;

  return (
    <Container>
      <AllEquipmentBackButton
        retainAssemblyView={!!selectedSpherical}
        changeTab={changeTab}
        sx={{ margin: '1.6rem 0' }}
      />
      <SearchBar
        value={searchTerm}
        onChange={handleSearchPoi}
        disabled={isLoading || pois.length === 0}
      />
      {isLoading && <PoiTypeAccordionSkeleton />}
      {!isLoading && Object.keys(filteredTemplates).length > 0 && (
        <ScrollableContainer>
          {filteredTemplates.map((template) => (
            <PoiTypeAccordion
              key={template.id}
              title={template.name}
              templateId={template.id}
              poiTemplateKey={template?.key}
              details={(statusViewFilter) => (
                <PointsOfInterestList
                  pointsOfInterest={checkedGroupedPois[template.id] ?? []}
                  type={template.name}
                  statusViewFilter={statusViewFilter}
                />
              )}
              defaultExpanded={false}
              count={checkedGroupedPois[template.id]?.length ?? 0}
            />
          ))}
        </ScrollableContainer>
      )}
      {!isLoading && (pois.length === 0 || Object.keys(filteredTemplates).length === 0) && (
        <Stack width="100%" alignItems="center" justifyContent="center" marginTop="3.2rem">
          <Typography variant="body2" color={abyssColors.neutral[400]}>
            No Points of Interest Found
          </Typography>
        </Stack>
      )}
      <AddToReportButton groupedPois={checkedGroupedPois} />
    </Container>
  );
};
